import type { AxiosResponse } from 'axios';
import { env } from '@/app/config/env';
import { HTTP_STATUS } from '@/app/config/constants';
import { createApiError } from '@/core/error-handling/ApiErrorHandler';
import { logger } from '@/core/logging/logger';
import { apiClient } from '@/shared/services/api/client';
import { delay } from '@/shared/utils/asyncUtils';
import {
  AUTH_MESSAGES,
  DEMO_OTP,
  MOCK_LATENCY_MS,
  OTP_RESEND_COOLDOWN_SECONDS,
  OTP_TTL_SECONDS,
} from '../constants';
import {
  getMockLoginResponse,
  getMockProfile,
  getMockRefreshResponse,
  getMockResendOtpResponse,
  getMockSendOtpResponse,
  getMockVerifyOtpResponse,
  isMockCurrentPassword,
} from '../__mocks__/mockUsers';
import type {
  ApiEnvelope,
  AuthTokens,
  ForgotPasswordRequest,
  LoginRequest,
  OtpErrorCode,
  OtpResendResponse,
  OtpSendResponse,
  OtpVerifyRequest,
  OtpWindow,
  PasswordResetIdentity,
  PasswordResetWindow,
  ChangePasswordRequest,
  ResetPasswordLinkRequest,
  ResetPasswordRequest,
  VerifyResetPasswordOtpRequest,
  VerifyResetPasswordLinkRequest,
  UserProfile,
} from '../models';

/**
 * The one-time-code paths the platform actually serves. Both are on its
 * public-path allow-list, so neither carries a bearer token -- see
 * AUTH_EXCLUDED_PATHS in shared/services/api/interceptors.ts, which must list
 * these same two values.
 *
 * Resend maps to the request path on purpose: the platform has no separate
 * resend endpoint. For the sign-in OTP a second request supersedes the
 * outstanding code. (Forgot Password differs: a request within the code's
 * validity window re-sends the same code without extending it -- see
 * `forgotPassword` below.)
 */
const OTP_REQUEST_PATH = '/auth/otp/request';
const OTP_VERIFY_PATH = '/auth/otp/verify';

/**
 * The supplier portal's one-time-code sign-in (2026-10-23): a code to the e-mail on the supplier's master record.
 * Under `/auth/otp/`, so public on the platform and bearer-free here alike. Refresh and logout are the ordinary ones.
 */
const SUPPLIER_OTP_REQUEST_PATH = '/auth/otp/supplier/request';
const SUPPLIER_OTP_VERIFY_PATH = '/auth/otp/supplier/verify';

/** Password recovery, likewise public and likewise on the interceptor's allow-list. */
const PASSWORD_FORGOT_PATH = '/auth/password/forgot';
const PASSWORD_RESET_PATH = '/auth/password/reset';
const PASSWORD_FORGOT_LINK_PATH = '/auth/password/forgot-link';
const PASSWORD_RESET_LINK_PATH = '/auth/password/reset-link';
const PASSWORD_VERIFY_RESET_OTP_PATH = '/auth/password/verify-otp';
const PASSWORD_VERIFY_RESET_LINK_PATH = '/auth/password/verify-link';

/**
 * Endpoints per mdfiles/loginapi.md. Paths are relative to VITE_API_BASE_URL,
 * which already carries the `/api/v1` prefix.
 */
export const authApi = {
  login: (data: LoginRequest) => apiClient.post<ApiEnvelope<AuthTokens>>('/auth/login', data),
  refresh: (refreshToken: string) =>
    apiClient.post<ApiEnvelope<AuthTokens>>('/auth/refresh', { refreshToken }),
  getProfile: () => apiClient.get<ApiEnvelope<UserProfile>>('/users/me'),
  logout: () => apiClient.post<ApiEnvelope<null>>('/auth/logout'),
  /**
   * 204 while the session is open; 401 once it has ended -- logged out elsewhere, or the user deleted in User
   * Management or Assigned Admin (2026-10-13). The interceptor turns that 401 into a sign-out, so a caller only
   * has to ask. See `useSessionWatch`.
   */
  checkSession: () => apiClient.get<void>('/auth/session'),

  /*
    The platform serves one request endpoint for both send and resend -- there
    is no separate resend route -- and answers with the code's window under
    `data`, plus the envelope's own `message`. Never the code itself.
  */
  requestOtp: (email: string) =>
    apiClient.post<ApiEnvelope<OtpWindow | null>>(OTP_REQUEST_PATH, { email }),
  verifyOtp: (payload: OtpVerifyRequest) =>
    apiClient.post<ApiEnvelope<AuthTokens>>(OTP_VERIFY_PATH, payload),
  /* The supplier portal's sign-in, the same shapes on its own paths. */
  requestSupplierOtp: (email: string) =>
    apiClient.post<ApiEnvelope<OtpWindow | null>>(SUPPLIER_OTP_REQUEST_PATH, { email }),
  verifySupplierOtp: (payload: OtpVerifyRequest) =>
    apiClient.post<ApiEnvelope<AuthTokens>>(SUPPLIER_OTP_VERIFY_PATH, payload),

  /*
    Password recovery. Both are public on the server -- a user who cannot sign in has no credential to
    present -- so neither carries a bearer token, and both must appear in AUTH_EXCLUDED_PATHS.

    The paths are `/auth/password/forgot` and `/auth/password/reset`, which is what PasswordResetController
    maps. They were `/auth/forgot-password` and `/auth/reset-password` here, which the platform has never
    served: bound and typed against a route that did not exist.
  */
  forgotPassword: (data: ForgotPasswordRequest) =>
    apiClient.post<ApiEnvelope<PasswordResetWindow>>(PASSWORD_FORGOT_PATH, data),
  forgotPasswordWithLink: (data: ForgotPasswordRequest) =>
    apiClient.post<ApiEnvelope<PasswordResetWindow>>(PASSWORD_FORGOT_LINK_PATH, data),
  resetPassword: (data: ResetPasswordRequest) =>
    apiClient.post<ApiEnvelope<null>>(PASSWORD_RESET_PATH, data),
  resetPasswordWithLink: (data: ResetPasswordLinkRequest) =>
    apiClient.post<ApiEnvelope<null>>(PASSWORD_RESET_LINK_PATH, data),
  verifyPasswordResetOtp: (data: VerifyResetPasswordOtpRequest) =>
    apiClient.post<ApiEnvelope<PasswordResetIdentity>>(PASSWORD_VERIFY_RESET_OTP_PATH, data),
  verifyPasswordResetLink: (data: VerifyResetPasswordLinkRequest) =>
    apiClient.post<ApiEnvelope<PasswordResetIdentity>>(PASSWORD_VERIFY_RESET_LINK_PATH, data),
  /** The self-service change; `/users/{id}/security/password` is the administrator's and does not ask for the current one. */
  changePassword: (userId: string, data: ChangePasswordRequest) =>
    apiClient.put<ApiEnvelope<null>>(`/users/${encodeURIComponent(userId)}/password`, data),
};

/* -------------------------------------------------------------------------- */

/**
 * Unwraps `{ success, data, message }`.
 *
 * A 2xx carrying `success: false` is still a failure, and the envelope's own
 * `message` is the most specific thing we can show a user, so it is promoted
 * into a properly shaped ApiError rather than being swallowed.
 */
function unwrap<T>(response: AxiosResponse<ApiEnvelope<T>>): T {
  const body = response.data;

  if (!body || typeof body !== 'object') {
    throw createApiError({
      message: AUTH_MESSAGES.UNEXPECTED_RESPONSE,
      code: 'SERVER_ERROR',
      status: response.status,
    });
  }

  // A described failure: the envelope's own message is the most specific
  // thing we can show, so it wins.
  if (body.success === false) {
    throw createApiError({
      message: body.message || AUTH_MESSAGES.UNEXPECTED_RESPONSE,
      code: 'SERVER_ERROR',
      status: response.status,
    });
  }

  // success: true with no payload is malformed, not a described failure --
  // echoing its message ("Request completed successfully") would mislead.
  if (body.data === undefined || body.data === null) {
    throw createApiError({
      message: AUTH_MESSAGES.UNEXPECTED_RESPONSE,
      code: 'SERVER_ERROR',
      status: response.status,
    });
  }

  return body.data;
}

/**
 * A server-reported countdown, or the fallback.
 *
 * Zero and negatives are rejected as well as non-numbers: a window of 0 would
 * render as an already-expired code and an already-available resend, which is
 * worse than falling back to the documented default.
 */
function positiveSeconds(value: number | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback;
}

function unauthorized(message: string): never {
  throw createApiError({
    message,
    code: 'AUTHENTICATION_ERROR',
    status: HTTP_STATUS.UNAUTHORIZED,
  });
}

function otpError(code: OtpErrorCode): never {
  const rateLimited = code === 'TOO_MANY_ATTEMPTS';
  throw createApiError({
    message: AUTH_MESSAGES[code],
    code: rateLimited ? 'RATE_LIMIT_ERROR' : 'VALIDATION_ERROR',
    status: rateLimited ? HTTP_STATUS.TOO_MANY_REQUESTS : HTTP_STATUS.BAD_REQUEST,
  });
}

/** NB-547: the platform's refusal of POST /auth/otp/request for an address no user holds, as normalizeError shapes it. */
function emailNotRegistered(): never {
  throw createApiError({
    message: AUTH_MESSAGES.EMAIL_NOT_REGISTERED,
    code: 'VALIDATION_ERROR',
    status: HTTP_STATUS.BAD_REQUEST,
    details: { email: [AUTH_MESSAGES.EMAIL_NOT_REGISTERED] },
  });
}

if (env.ENABLE_MOCKS) {
  logger.info(`Mocks enabled. OTP for any demo account: ${DEMO_OTP}`);
}

/**
 * The service layer the sagas talk to -- the only module in the feature that
 * knows mocks exist (loginapi.md section 8: "Mock mode enabled | Use mock
 * responses instead of real API calls").
 *
 * Mock failures are thrown as properly shaped ApiErrors so they travel the
 * same normalizeError -> getErrorMessage path as real ones.
 */
export const authService = {
  /** Step 1: POST /auth/login -> tokens. */
  async login(data: LoginRequest): Promise<AuthTokens> {
    if (env.ENABLE_MOCKS) {
      await delay(MOCK_LATENCY_MS);
      return (
        getMockLoginResponse(data.username, data.password) ??
        unauthorized(AUTH_MESSAGES.INVALID_CREDENTIALS)
      );
    }
    return unwrap(await authApi.login(data));
  },

  /**
   * Step 3: GET /users/me -> profile.
   *
   * `username` is only consulted in mock mode; the real call is authorised by
   * the bearer token the interceptor attaches.
   */
  async getProfile(username?: string): Promise<UserProfile> {
    if (env.ENABLE_MOCKS) {
      await delay(MOCK_LATENCY_MS / 2);
      return getMockProfile(username) ?? unauthorized(AUTH_MESSAGES.SESSION_EXPIRED);
    }
    return unwrap(await authApi.getProfile());
  },

  /** Step 6: POST /auth/refresh -> rotated tokens. */
  async refresh(refreshToken: string): Promise<AuthTokens> {
    if (env.ENABLE_MOCKS) {
      await delay(MOCK_LATENCY_MS / 2);
      return (
        getMockRefreshResponse(refreshToken) ?? unauthorized(AUTH_MESSAGES.SESSION_EXPIRED)
      );
    }
    return unwrap(await authApi.refresh(refreshToken));
  },

  /** Step 7: POST /auth/logout. A failure must not block local cleanup. */
  async logout(): Promise<void> {
    if (env.ENABLE_MOCKS) {
      await delay(MOCK_LATENCY_MS / 2);
      return;
    }
    await authApi.logout();
  },

  /* -- OTP ---------------------------------------------------------------- */
  /*
    Wired to the platform's real endpoints and honouring ENABLE_MOCKS like
    every other function here. Since NB-547 the request endpoint no longer
    answers identically for every address: one no (non-deleted) user holds is
    refused with a 400 VALIDATION_ERROR -- "This email address is not
    registered. Please enter a registered email address." on the `email` field
    -- and no code is sent, matching Forgot Password. That is a deliberate
    trade-off by the product owner: the request step now says whether an
    address has an account. The refusal travels the ordinary normalizeError ->
    getErrorMessage path, so the screen shows the server's own message and
    stays on the email step. Resend is the same request and is refused alike.

    NB-548: a request (Resend included) made while the outstanding sign-in
    code is still valid re-sends the SAME code without extending it, and
    `expiresIn` is the seconds that code has left -- so the countdown carries
    on from where it was rather than restarting. Only once the code has
    expired (or been used or locked) does a request issue a new one.

    Both windows come from the server, which reports its own
    `platform.identity.otp-ttl` and `platform.identity.otp-resend-interval`.
    They used to be client constants kept in step by hand; the constants are now
    only the fallback for a server that reports no window, since a missing
    window must not stop someone signing in.
  */

  async sendOtp(email: string): Promise<OtpSendResponse> {
    if (env.ENABLE_MOCKS) {
      await delay(MOCK_LATENCY_MS);
      return getMockSendOtpResponse(email) ?? emailNotRegistered();
    }
    const { data } = await authApi.requestOtp(email);
    const window: OtpWindow | null = data?.data ?? null;
    return {
      success: true,
      message: data?.message ?? AUTH_MESSAGES.OTP_SENT,
      expiresIn: positiveSeconds(window?.expiresIn, OTP_TTL_SECONDS),
      resendAfter: positiveSeconds(window?.resendAfter, OTP_RESEND_COOLDOWN_SECONDS),
    };
  },

  /** Resend is another request: the platform has no separate resend route. */
  async resendOtp(email: string): Promise<OtpResendResponse> {
    if (env.ENABLE_MOCKS) {
      await delay(MOCK_LATENCY_MS);
      return getMockResendOtpResponse(email) ?? emailNotRegistered();
    }
    return authService.sendOtp(email);
  },

  async verifyOtp(payload: OtpVerifyRequest): Promise<AuthTokens> {
    if (env.ENABLE_MOCKS) {
      await delay(MOCK_LATENCY_MS);
      const result = getMockVerifyOtpResponse(payload.email, payload.otp);
      /* The platform refuses a wrong or expired sign-in code with a 401, which must not read as a session ending. */
      if (!result.ok && (result.code === 'INVALID_OTP' || result.code === 'OTP_EXPIRED')) {
        unauthorized(AUTH_MESSAGES[result.code]);
      }
      if (!result.ok) otpError(result.code);
      return result.data;
    }
    return unwrap(await authApi.verifyOtp(payload));
  },

  /*
    The supplier portal (2026-10-23): the code goes to the address on the supplier's master record, and an address no
    Active supplier holds is refused on the e-mail step, as the staff sign-in refuses an unregistered one. Mock mode has
    no supplier accounts, so it says so rather than inventing one.
  */
  async sendSupplierOtp(email: string): Promise<OtpSendResponse> {
    if (env.ENABLE_MOCKS) {
      await delay(MOCK_LATENCY_MS);
      return unauthorized(AUTH_MESSAGES.SUPPLIER_MOCK_UNAVAILABLE);
    }
    const { data } = await authApi.requestSupplierOtp(email);
    const window: OtpWindow | null = data?.data ?? null;
    return {
      success: true,
      message: data?.message ?? AUTH_MESSAGES.OTP_SENT,
      expiresIn: positiveSeconds(window?.expiresIn, OTP_TTL_SECONDS),
      resendAfter: positiveSeconds(window?.resendAfter, OTP_RESEND_COOLDOWN_SECONDS),
    };
  },

  async verifySupplierOtp(payload: OtpVerifyRequest): Promise<AuthTokens> {
    if (env.ENABLE_MOCKS) {
      await delay(MOCK_LATENCY_MS);
      return unauthorized(AUTH_MESSAGES.SUPPLIER_MOCK_UNAVAILABLE);
    }
    return unwrap(await authApi.verifySupplierOtp(payload));
  },

  /* -- password recovery --------------------------------------------------- */
  /*
    The server checks the user-detail table before sending a reset code or link and rejects unregistered
    addresses. The request methods are also used for resending, so each resend repeats the same check.

    Forgot Password (OTP): a request made while the outstanding code is still valid (5 minutes) re-sends the
    SAME code without extending it, and `expiresIn` is the seconds that code has left -- not a fresh window.
    Only once it has expired does a request issue a new code. The sign-in OTP behaves the same way (NB-548).
  */

  async forgotPassword(data: ForgotPasswordRequest): Promise<PasswordResetWindow> {
    if (env.ENABLE_MOCKS) {
      await delay(MOCK_LATENCY_MS);
      return { expiresIn: OTP_TTL_SECONDS, resendAfter: OTP_RESEND_COOLDOWN_SECONDS };
    }
    return unwrap(await authApi.forgotPassword(data));
  },

  async forgotPasswordWithLink(data: ForgotPasswordRequest): Promise<PasswordResetWindow> {
    if (env.ENABLE_MOCKS) {
      await delay(MOCK_LATENCY_MS);
      return { expiresIn: OTP_TTL_SECONDS, resendAfter: OTP_RESEND_COOLDOWN_SECONDS };
    }
    return unwrap(await authApi.forgotPasswordWithLink(data));
  },

  /**
   * A password the policy or the history rejects does not consume the code, so the user can try another
   * without requesting a new one. The screen keeps them on the form rather than sending them back a step.
   */
  async resetPassword(data: ResetPasswordRequest): Promise<void> {
    if (env.ENABLE_MOCKS) {
      await delay(MOCK_LATENCY_MS);
      if (data.otp !== DEMO_OTP) otpError('INVALID_OTP');
      return;
    }
    await authApi.resetPassword(data);
  },

  async resetPasswordWithLink(data: ResetPasswordLinkRequest): Promise<void> {
    if (env.ENABLE_MOCKS) {
      await delay(MOCK_LATENCY_MS);
      return;
    }
    await authApi.resetPasswordWithLink(data);
  },

  async verifyPasswordResetOtp(data: VerifyResetPasswordOtpRequest): Promise<PasswordResetIdentity> {
    if (env.ENABLE_MOCKS) {
      await delay(MOCK_LATENCY_MS);
      if (data.otp !== DEMO_OTP) otpError('INVALID_OTP');
      return { firstName: 'Jane', lastName: 'Doe' };
    }
    return unwrap(await authApi.verifyPasswordResetOtp(data));
  },

  async verifyPasswordResetLink(data: VerifyResetPasswordLinkRequest): Promise<PasswordResetIdentity> {
    if (env.ENABLE_MOCKS) {
      await delay(MOCK_LATENCY_MS);
      return { firstName: 'Jane', lastName: 'Doe' };
    }
    return unwrap(await authApi.verifyPasswordResetLink(data));
  },

  /**
   * Changes the signed-in user's own password. A void envelope: `success: false` is a refusal and throws,
   * like every other call here, so the screen reports the server's own message (the reuse rule, the
   * whitespace rule).
   *
   * A wrong Old Password is a 400 VALIDATION_ERROR -- not a 401, so it never reads as an expired session --
   * carrying "The old password is incorrect." on the `currentPassword` field; normalizeError exposes it as
   * `details.currentPassword`. The password values are sent exactly as typed, never trimmed.
   */
  async changePassword(userId: string, data: ChangePasswordRequest): Promise<void> {
    if (env.ENABLE_MOCKS) {
      await delay(MOCK_LATENCY_MS);
      if (!isMockCurrentPassword(userId, data.currentPassword)) {
        throw createApiError({
          message: AUTH_MESSAGES.OLD_PASSWORD_INCORRECT,
          code: 'VALIDATION_ERROR',
          status: HTTP_STATUS.BAD_REQUEST,
          details: { currentPassword: [AUTH_MESSAGES.OLD_PASSWORD_INCORRECT] },
        });
      }
      if (/\s/.test(data.newPassword)) {
        throw createApiError({
          message: 'Password must not contain spaces',
          code: 'VALIDATION_ERROR',
          status: HTTP_STATUS.BAD_REQUEST,
          details: { newPassword: ['Password must not contain spaces'] },
        });
      }
      return;
    }
    const response = await authApi.changePassword(userId, data);
    if (response.data && typeof response.data === 'object' && response.data.success === false) {
      throw createApiError({
        message: response.data.message || AUTH_MESSAGES.UNEXPECTED_RESPONSE,
        code: 'SERVER_ERROR',
        status: response.status,
      });
    }
  },
};
